<script setup lang="ts">
import { AppButton, AppFlicker, AppModal } from '@libs/ui';

import type { PeekPriceModalItineraryData } from '../models';

interface PeekPriceModalDesktopLayoutProps {
  itinerary: PeekPriceModalItineraryData;
}

const props = defineProps<PeekPriceModalDesktopLayoutProps>();

interface PeekPriceModalDesktopLayoutEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
  (eventName: 'select-date', date: Date): void;
  (eventName: 'select-month', date: Date): void;
}

const emit = defineEmits<PeekPriceModalDesktopLayoutEmits>();
</script>

<template>
  <AppModal @close="emit('cancel')">
    <div class="peek-price-modal-desktop-layout">
      <div class="peek-price-modal-desktop-layout-body">
        <div class="body-itinerary">
          <slot name="itinerary" />
        </div>
        <div class="body-separator" />
        <div class="body-calendar">
          <slot name="priceCalendar" />
        </div>
      </div>

      <div class="peek-price-modal-desktop-footer">
        <div class="footer-summary">
          <div class="summary-text">
            {{ $t('planner.total_trip_price_for') }}
            {{ props.itinerary.paxes }},
            <template v-if="props.itinerary.dates">
              {{ props.itinerary.dates }}
            </template>
            <AppFlicker
              v-else
              height="100%"
              width="spacing-96"
              class="dates-flicker"
            />
          </div>
          <div v-if="props.itinerary.price" class="price-text">
            {{ props.itinerary.price }}
          </div>
          <div v-else class="price-flicker">
            <AppFlicker height="100%" width="spacing-96" />
          </div>
        </div>
        <div class="footer-actions">
          <AppButton
            type="secondary"
            state="enabled"
            size="large"
            @click="emit('cancel')"
          >
            {{ $t('planner.close') }}
          </AppButton>
          <AppButton
            type="primary"
            state="enabled"
            size="large"
            @click="emit('confirm')"
          >
            {{ $t('planner.select') }}
          </AppButton>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<style scoped lang="scss">
.peek-price-modal-desktop-layout {
  height: 100%;

  display: flex;
  flex-direction: column;

  .peek-price-modal-desktop-layout-body {
    flex: 1;
    overflow: hidden;
    padding: var(--spacing-24) var(--spacing-24) 0 var(--spacing-24);

    display: grid;
    grid-template-columns: 1fr var(--spacing-24) 1px var(--spacing-24) 1fr;
    grid-template-rows: 100%;

    .body-itinerary {
      grid-column: 1/2;
      overflow-y: auto;
      padding-bottom: var(--spacing-24);
    }

    .body-separator {
      grid-column: 3/4;
      height: 100%;
      width: 1px;
      background-color: var(--color-neutral-20);
    }

    .body-calendar {
      grid-column: 5/6;
      height: 100%;
    }
  }

  .peek-price-modal-desktop-footer {
    border-top: 1px solid var(--color-neutral-20);
    padding: var(--spacing-16) var(--spacing-24) var(--spacing-16)
      var(--spacing-24);

    display: flex;

    .footer-summary {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .summary-text {
        @include font-label-l-light;
        color: var(--color-neutral-70);
        display: flex;
        align-items: center;

        .dates-flicker {
          margin-left: var(--spacing-4);
        }
      }

      .price-text {
        @include font-heading-3;
        color: var(--color-soft-black);
      }

      .price-flicker {
        --price-text-height: 35px;
        height: var(--price-text-height);
      }
    }

    .footer-actions {
      display: flex;
      gap: var(--spacing-24);
    }
  }
}
</style>
